import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableRowClickEvent,
  DataTableRowReorderEvent,
  DataTableSelectionSingleChangeEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { TaskStatusTemplate } from '../constants/TaskStatusTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import useTaskStatusesService from '../hooks/services/useTaskStatusesService';
import { ruLocale } from '../lang/ruLocale';
import { BaseFilter, TaskStatus } from '../types/interfaces';
import TaskStatusCard from '../ui/TaskStatusCard';
import { clearFilter, generateTags } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

export default function TaskStatuses() {
  const [statusName, setStatusName] = useState<TaskStatus[]>([]);
  const [filtredTaskStatuses, setFiltredTaskStatuses] = useState<TaskStatus[]>(
    []
  );
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [taskStatusToDelete, setTaskStatusToDelete] = useState<
    string | number | null
  >(null);
  const [selectedTaskStatus, setSelectedTaskStatus] =
    useState<TaskStatus>(TaskStatusTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    statusName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskCloseCodes: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const taskCloseCodesFilterRef = useRef<HTMLInputElement>(null);
  const statusNameFilterRef = useRef<HTMLInputElement>(null);
  const globalFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const taskStatusCardRef = useRef<null | { handleCancel: () => void }>(null);
  const [totalCount, setTotalCount] = useState(1);
  const isSelectedIdExist = isSelected(selectedTaskStatus);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const taskStatusesService = useTaskStatusesService();

  const getTaskStatusesCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const baseFilters: BaseFilter[] = [];
    const globalValue = (filters.global as DataTableFilterMetaData).value;
    const statusNameValue = (filters.statusName as DataTableFilterMetaData)
      .value;
    const taskCloseCodesValue = (
      filters.taskCloseCodes as DataTableFilterMetaData
    ).value;
    baseFilters.push({
      field: 'global',
      value: globalValue === '' ? undefined : globalValue,
      operator: FilterMatchMode.CONTAINS
    });
    baseFilters.push({
      field: 'statusName',
      value: statusNameValue === '' ? undefined : statusNameValue,
      operator: (filters.statusName as DataTableFilterMetaData).matchMode
    });
    baseFilters.push({
      field: 'taskCloseCodes',
      value: taskCloseCodesValue === '' ? undefined : taskCloseCodesValue,
      operator: (filters.taskCloseCodes as DataTableFilterMetaData).matchMode
    });
    const serviceResult = taskStatusesService.getAll({
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(baseFilters)
    });
    serviceResult.result.then((r) => {
      if (r) {
        setStatusName(r?.itemsList);
        setFiltredTaskStatuses(r.itemsList);
        setLoading(false);
        setTotalCount((r.total === 0 ? 1 : r.total) ?? 1);
      }
    });

    return serviceResult.cancel;
  }, [filters, multiSortMeta, taskStatusesService]);

  useEffect(() => {
    const cancel = getTaskStatusesCallback();
    return () => cancel();
  }, [getTaskStatusesCallback]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedTaskStatus(TaskStatusTemplate);
    setDialogVisible(true);
  };

  const handleDeleteConfirmation = () => {
    handleDelete(taskStatusToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: string | number,
    updatedStatuses: TaskStatus
  ) => {
    const result =
      await taskStatusesService.addOrUpdate(updatedStatuses)?.result;
    getTaskStatusesCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await taskStatusesService.delete(parseInt(id.toString()))?.result;
      getTaskStatusesCallback();
    }
  };

  const handleAdd = async (statusName: TaskStatus) => {
    const result = await taskStatusesService.addOrUpdate(statusName)?.result;
    getTaskStatusesCallback();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideTaskStatusCard = () => {
    if (taskStatusCardRef.current !== null) {
      taskStatusCardRef.current.handleCancel();
    }
  };
  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите статус наряда для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  };

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setTaskStatusToDelete(selectedTaskStatus.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите статус наряда для удаления',
        life: 3000
      });
    }
  }

  const header = (  
    <TablePageHeader
      tableName="task-statuses"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const onSelectionChangeCallback = useCallback(
    (e: DataTableSelectionSingleChangeEvent<TaskStatus[]>) =>
      setSelectedTaskStatus(e.value),
    []
  );

  const onRowDoubleClickCallback = useCallback((e: DataTableRowClickEvent) => {
    setDialogVisible(true);
    setSelectedTaskStatus(e.data as TaskStatus);
  }, []);

  const onRowReorderCallback = useCallback(
    (e: DataTableRowReorderEvent<TaskStatus[]>) => setStatusName(e.value),
    []
  );

  return (
    <div className="statusName-table wrapper wrapper--main">
      <Toast ref={toast} />
      <Dialog
        header="Статус наряда"
        visible={dialogVisible}
        style={{ minWidth: '600px', width: '600px', minHeight: '556px' }}
        maximizable
        modal
        onHide={() => onHideTaskStatusCard()}
      >
        <VisibilityWrapper code="task-status-card__roles">
          <TaskStatusCard
            taskStatusProps={selectedTaskStatus}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={taskStatusCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="cтатус наряда"
        entityName={selectedTaskStatus?.statusName}
      />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedTaskStatus!}
        onSelectionChange={onSelectionChangeCallback}
        onRowDoubleClick={onRowDoubleClickCallback}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={filtredTaskStatuses}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        // globalFilterFields={['taskCloseCodes', 'statusName']}
        header={header}
        emptyMessage="Статусы нарядов не найдены."
        onRowReorder={onRowReorderCallback}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
        footer={
          <div className="p-paginator p-component p-paginator-bottom justify-content-end" style={{
            height: 60
          }}>
            <span>{`Всего: ${filtredTaskStatuses.length > 0 ? totalCount : 0}`}</span>
          </div>
        }
        pt={{
          footer: {
            style: {
              padding: 'unset',
              border: 'unset'
            }
          }
        }}
      >
        <Column
          columnKey="statusName"
          sortable
          field="statusName"
          header="Название статуса"
          filter
          onFilterClear={() =>
            clearFilter('statusName', filters, setFilters, statusNameFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={statusNameFilterRef}
                onChange={handleInputChange('statusName')}
                placeholder="Поиск по названию статуса"
              />
            </div>
          }
          style={{ width: '33%' }}
        />
        <Column
          columnKey="taskCloseCodes"
          field="taskCloseCodes"
          header="Шифры закрытия нарядов"
          body={(taskStatusData: TaskStatus | undefined) =>
            generateTags(taskStatusData, 'taskCloseCodes')
          }
          filter
          onFilterClear={() =>
            clearFilter(
              'taskCloseCodes',
              filters,
              setFilters,
              taskCloseCodesFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={taskCloseCodesFilterRef}
                onChange={handleInputChange('taskCloseCodes')}
                placeholder="Поиск по шифрам закрытия нарядов"
              />
            </div>
          }
          style={{ width: '65%' }}
        />
      </DataTable>
    </div>
  );
}
